// Global render-error boundary.
//
// Without this, a single component throwing during render unmounts the whole tree
// and the user sees a blank white page with no way forward (this is exactly how a
// react-query cache-shape collision presented as "scheduling a session shows a
// blank screen"). Catch it, log it, and give the user a way out.

import { Component } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] uncaught render error:", error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              This page hit an unexpected error. Reloading usually fixes it.
            </p>
          </div>
          {import.meta.env.DEV && (
            <pre className="max-h-40 overflow-auto rounded-lg bg-muted/50 p-3 text-left text-xs text-muted-foreground">
              {String(error?.stack || error?.message || error)}
            </pre>
          )}
          <div className="flex justify-center gap-2">
            <Button onClick={() => window.location.reload()}>Reload page</Button>
            <Button variant="outline" onClick={() => { window.location.href = "/"; }}>
              Go home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
